/**
 * Google Consent Mode v2 — bridge our consent state with Google tags
 * (GTM, GA4, Google Ads) loaded via Webflow custom code.
 *
 * Pourquoi : les scripts Google (gtag, GTM) sont chargés via le head
 * Webflow et fire AVANT notre runtime. Pour rester RGPD compliant, on
 * utilise le Consent Mode v2 (officiel Google) :
 *
 *   1. Le snippet AVANT GTM (à coller dans Webflow head) pose un
 *      "default deny" pour TOUS les consents :
 *
 *      <script>
 *      window.dataLayer = window.dataLayer || [];
 *      function gtag(){dataLayer.push(arguments);}
 *      gtag('consent', 'default', {
 *        ad_storage: 'denied',
 *        ad_user_data: 'denied',
 *        ad_personalization: 'denied',
 *        analytics_storage: 'denied',
 *        functionality_storage: 'denied',
 *        personalization_storage: 'denied',
 *        security_storage: 'granted',  // toujours OK (sécurité)
 *        wait_for_update: 500
 *      });
 *      </script>
 *
 *   2. GTM/GA4/Ads scripts chargent normalement, mais NE POSENT AUCUN
 *      cookie ni ne fire aucun event tant que consent_default=denied.
 *
 *   3. Notre runtime LMDR fire `gtag('consent', 'update', {...})` à
 *      chaque changement de consent (smootify:consent_changed event)
 *      avec les bonnes valeurs `granted` / `denied`.
 *
 * Référence : https://developers.google.com/tag-platform/security/guides/consent
 */

import { analyticsAllowed, marketingAllowed, preferencesAllowed } from "./consent.js";

interface GtagConsentParams {
  ad_storage?: "granted" | "denied";
  ad_user_data?: "granted" | "denied";
  ad_personalization?: "granted" | "denied";
  analytics_storage?: "granted" | "denied";
  functionality_storage?: "granted" | "denied";
  personalization_storage?: "granted" | "denied";
  security_storage?: "granted" | "denied";
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function ensureGtag(): boolean {
  if (typeof window === "undefined") return false;
  // Si gtag déjà défini → OK
  if (typeof window.gtag === "function") return true;
  // Si dataLayer existe (snippet bootstrap déjà posé) → définir notre gtag wrapper
  if (Array.isArray(window.dataLayer)) {
    window.gtag = function (...args: unknown[]): void {
      (window.dataLayer as unknown[]).push(args);
    };
    return true;
  }
  // Aucun dataLayer → on crée + bootstrap
  window.dataLayer = [];
  window.gtag = function (...args: unknown[]): void {
    (window.dataLayer as unknown[]).push(args);
  };
  return true;
}

function buildConsentParams(): GtagConsentParams {
  const ana = analyticsAllowed();
  const mkt = marketingAllowed();
  const pref = preferencesAllowed();
  return {
    ad_storage: mkt ? "granted" : "denied",
    ad_user_data: mkt ? "granted" : "denied",
    ad_personalization: mkt ? "granted" : "denied",
    analytics_storage: ana ? "granted" : "denied",
    functionality_storage: pref ? "granted" : "denied",
    personalization_storage: pref ? "granted" : "denied",
    security_storage: "granted", // toujours OK (sécurité fonctionnelle)
  };
}

/**
 * Push `gtag('consent', 'update', {...})` à dataLayer avec le state actuel.
 * Appelé au boot (sync l'état initial) + à chaque smootify:consent_changed.
 */
export function syncConsentToGoogle(): void {
  if (!ensureGtag()) return;
  const params = buildConsentParams();
  // gtag('consent', 'update', params) — Consent Mode v2 syntax
  window.gtag?.("consent", "update", params);
}

/**
 * Au boot : sync immédiatement (couvre le cas où l'user a déjà un consent
 * stocké d'une session précédente). Listener pour re-sync à chaque change.
 *
 * Idempotent.
 */
export function attachConsentMode(): void {
  if (typeof document === "undefined") return;
  if ((document as unknown as { __lmdrConsentModeBound?: boolean }).__lmdrConsentModeBound) return;
  (document as unknown as { __lmdrConsentModeBound?: boolean }).__lmdrConsentModeBound = true;

  syncConsentToGoogle();
  document.addEventListener("smootify:consent_changed", () => {
    syncConsentToGoogle();
  });
}
